import { QueryResult } from 'react-query';
import moment from 'moment-timezone';
import { defaultSmDate } from 'utils/formatter';
import { callApi, Controller, useApi, UseApiOptions } from './useApi';
import { addLieferungPosition, defaultLieferungPosition, LieferungPosition } from './lieferungPosition';
// eslint-disable-next-line import/no-cycle
import { getKunde, Kunde } from './kunde';
// eslint-disable-next-line import/no-cycle
import { Zusatzadresse } from './zusatzadresse';
// eslint-disable-next-line import/no-cycle
import { AuftragPosition } from './auftragPosition';
// eslint-disable-next-line import/no-cycle
import { Auftrag } from './auftrag';

export interface Lieferung {
    kNr?: number;
    nr: string;
    kaNr: number;
    kaName: string;
    auNr: string;
    lieferAdrNr: number;
    status: number;
    datum: string;
    datBis: string;
    rahmen: boolean;
    betreuer: number;
    txt: string;
    txtB1: number;
    txtB2: number;
    ara: number;
    liefBed: number;
    liefZeit: number;
    liefDat: string;
    liefKw: number;
    zahlBed: number;
    mwstCode: number;
    sNetto: number;
    sMwst: number;
    sBrutto: number;
    aktText: string;
    abrech: boolean;
    formular: number;
    anlageDat?: string;
    anlageUhr?: number;
    anlageUser?: string;
    updDat?: string;
    updUhr?: number;
    updUser?: string;

    kunde?: Kunde;
    lieferKunde?: Kunde;
    lieferAdr?: Zusatzadresse;
    rechnungsKunde?: Kunde;
    rechnungsAdr?: Zusatzadresse;
    auftrag?: Auftrag;
}

export function getDefaultLieferung(kNr?: number): Lieferung {
    const values: Lieferung = {
        kNr: undefined,
        nr: '',
        kaNr: 0,
        auNr: '',
        lieferAdrNr: 0,
        kaName: '',
        status: 0,
        datum: defaultSmDate,
        datBis: defaultSmDate,
        rahmen: false,
        betreuer: 0,
        txt: '',
        txtB1: 1,
        txtB2: 2,
        ara: 0,
        liefBed: 17,
        liefDat: defaultSmDate,
        liefZeit: 0,
        liefKw: 0,
        zahlBed: 1,
        mwstCode: 0,
        sNetto: 0,
        sMwst: 0,
        sBrutto: 0,
        aktText: '',
        abrech: false,
        formular: 0,
    };

    const now = moment();

    return {
        ...values,
        kNr,
        datum: now.format('YYYY-MM-DD') + 'T00:00:00',
        // datBis: now.clone().add({ days: 7 }).format('YYYY-MM-DD') + 'T00:00:00',
    };
}

export interface LieferungFilter {
    nr?: string;
    nrSearch?: string;
    kNr?: number;
    auNr?: string;
    auNrList?: string[];
    auNrSearch?: string;
    artikelGruppeNr?: number;
    artikelNr?: string;
    searchText?: string;
    betreuer?: number;
    datumVon?: string;
    datumBis?: string;
    lArt?: string;
    status?: number;
    statusList?: number[];
    withKunde?: boolean;
    withAuftrag?: boolean;
    maxRows?: number;
    sortAsc?: boolean;
}

export function useLieferung(filter: LieferungFilter, apiOptions?: UseApiOptions<Lieferung>): QueryResult<Lieferung> {
    return useApi<Lieferung>(Controller.lieferung + '/one', { filter }, apiOptions);
}

export function useLieferungList(
    filter: LieferungFilter,
    apiOptions?: UseApiOptions<Lieferung[]>,
): QueryResult<Lieferung[]> {
    return useApi<Lieferung[]>(Controller.lieferung + '/list', { filter }, apiOptions);
}

interface AddLieferungResult {
    kNr: number;
    newNr: string;
}

export async function addLieferung(lieferung: Lieferung): Promise<AddLieferungResult> {
    return callApi<AddLieferungResult>(Controller.lieferung + '/add', { lieferung });
}

export async function updateLieferung(lieferung: Lieferung): Promise<void> {
    return callApi<void>(Controller.lieferung + '/update', { lieferung });
}

export async function deleteLieferung(lieferung: Lieferung): Promise<void> {
    return callApi<void>(Controller.lieferung + '/delete', { lieferung });
}

export async function sendLieferung(lieferung: Lieferung, kaNrListTo: number[], kaNrListCC: number[]): Promise<void> {
    return callApi<void>(Controller.lieferung + '/send', { nr: lieferung.nr, kaNrListTo, kaNrListCC });
}

/*
export async function bookLieferung(lieferung: Lieferung): Promise<void> {
    return callApi<void>(Controller.lieferung + '/book', { lieferung });
}
*/

export async function addLieferungFromAuftrag(
    auftrag: Auftrag,
    auftragPositionen?: AuftragPosition[],
): Promise<AddLieferungResult> {
    const defaultLieferung = getDefaultLieferung();

    const kNr = auftrag.lieferKNr ? auftrag.lieferKNr : auftrag.kNr || 0;

    const newLieferung: Lieferung = {
        ...defaultLieferung,
        kNr,
        kaNr: auftrag.kaNr,
        auNr: auftrag.nr,
        lieferAdrNr: auftrag.lieferKNr ? defaultLieferung.lieferAdrNr : auftrag.lieferAdrNr,
        betreuer: auftrag.betreuer,
        txt: auftrag.txt,
        aktText: auftrag.aktText,
        liefBed: auftrag.liefBed,
        liefDat: auftrag.liefDat,
        liefZeit: auftrag.liefZeit,
        liefKw: auftrag.liefKw,
        ara: auftrag.ara,
        zahlBed: auftrag.zahlBed,
        mwstCode: auftrag.mwstCode,
    };

    const result = await addLieferung(newLieferung);

    // let offenTotal = 0;

    if (auftragPositionen) {
        let pos = 1;
        // eslint-disable-next-line no-restricted-syntax
        for (const auftragPosition of auftragPositionen) {
            if (auftragPosition.transfer) {
                // eslint-disable-next-line no-await-in-loop
                await addLieferungPosition({
                    ...defaultLieferungPosition,
                    kNr,
                    lNr: result.newNr,
                    pos,
                    artikelGruppeNr: auftragPosition.artikelGruppeNr,
                    artikelNr: auftragPosition.artikelNr,
                    posText: auftragPosition.posText,
                    posEinh: auftragPosition.posEinh,
                    posMenge: auftragPosition.transferMenge || 0,
                    posAMenge: auftragPosition.posMenge,
                    posEkPreis: auftragPosition.posEkPreis,
                    posEPreis: auftragPosition.posEPreis,
                    posNetto: auftragPosition.posNetto,
                    posMwst: auftragPosition.posMwst,
                    posBrutto: auftragPosition.posBrutto,
                    posBem: auftragPosition.posBem,
                    offen: auftragPosition.posMenge,
                    lArt: auftragPosition.transferLArt || '',
                });
                pos += 1;

                // offenTotal += auftragPosition.offen - (auftragPosition.transferMenge || 0);
            }
        }
    }

    /*
    if (offenTotal > 0 && auftrag.status !== 9) {
        await updateAuftrag({
            ...auftrag,
            status: 9, // Teillieferung
        });
    } else if (offenTotal === 0 && auftrag.status !== 4) {
        await updateAuftrag({
            ...auftrag,
            status: 10, // geliefert
        });
    }
     */

    return {
        kNr,
        newNr: result.newNr,
    };
}

export async function copyLieferung(
    kNr: number,
    lieferung: Lieferung,
    lieferungPositionen?: LieferungPosition[],
): Promise<AddLieferungResult> {
    const newKundeList = await getKunde({ nr: kNr });

    if (newKundeList.length !== 1) {
        throw new Error('Kunde nicht gefunden');
    }

    const newKunde = newKundeList[0];

    const defaultLieferung = getDefaultLieferung();

    const newLieferung = {
        ...lieferung,
        kNr,
        status: 0,
        datum: defaultLieferung.datum,
        datBis: defaultLieferung.datBis,
        betreuer: newKunde.betreuer || defaultLieferung.betreuer,
        zahlBed: newKunde.zahlZiel || defaultLieferung.zahlBed,
    };

    const result = await addLieferung(newLieferung);

    if (lieferungPositionen) {
        // eslint-disable-next-line no-restricted-syntax
        for (const lieferungPosition of lieferungPositionen) {
            // eslint-disable-next-line no-await-in-loop
            await addLieferungPosition({ ...lieferungPosition, lNr: result.newNr });
        }
    }

    return result;
}

interface RecalculateLieferungResult {
    lieferung: Lieferung;
}

export async function recalculateLieferung(lieferung: Lieferung): Promise<RecalculateLieferungResult> {
    return callApi<RecalculateLieferungResult>(Controller.lieferung + '/recalculate', { lieferung });
}

interface FinalizeLieferungResult {
    lieferung: Lieferung;
}

export async function finalizeLieferung(lieferung: Lieferung): Promise<FinalizeLieferungResult> {
    return callApi<FinalizeLieferungResult>(Controller.lieferung + '/finalize', { lieferung });
}

interface MarkLieferungForExportResult {
    lieferung: Lieferung;
}

export async function markLieferungForExport(lieferung: Lieferung): Promise<MarkLieferungForExportResult> {
    return callApi<MarkLieferungForExportResult>(Controller.lieferung + '/markforexport', { lieferung });
}
